import moment from "moment/moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import { FiFileText, FiGitBranch, FiPauseCircle, FiPlayCircle, FiPlus, FiSearch } from "react-icons/fi";
import Layout from "../../../components/layouts/Layout";
import {
  Table,
  TableBody,
  TableHeader,
  TablePagination,
} from "../../../components/table";
import {
  duplicateAutomationFlow,
  fetchAutomationFlows,
  fetchLiveEvents,
  pauseAutomationFlow,
  publishAutomationFlow,
  SMS_AUTOMATION_FLOW_ENDPOINT,
} from "../AutomationTemplates/automationTemplateApi";
import SmsAccessGuard from "../Components/SmsAccessGuard";
import LocalStorage from "../../../hook/LocalStorage";
import { normaliseAutomationFlowStatus } from "../AutomationTemplates/automationFlowStatus";
import { readAutomationFlowReplyCount } from "../AutomationTemplates/automationFlowMetrics";
import "../style/sms-billing.css";

const FLOW_STATUS = {
  active: "Active",
  inactive: "Inactive",
};

const readFirstDefined = (source, keys) => {
  if (!source) {
    return null;
  }

  const key = keys.find((item) => source[item] !== undefined && source[item] !== null);

  return key ? source[key] : null;
};

const normaliseFlow = (flow) => {
  const template = readFirstDefined(flow, ["template", "automation_template", "automationTemplate"]) || {};
  const event = readFirstDefined(flow, ["event", "event_details", "eventDetails"]) || {};
  const status = normaliseAutomationFlowStatus(flow);
  const eventId =
    readFirstDefined(flow, ["event_id", "eventId"]) ||
    readFirstDefined(event, ["id", "event_id", "eventId"]);

  return {
    ...flow,
    id: readFirstDefined(flow, ["id", "flow_id", "flowId", "automation_flow_id", "automationFlowId"]),
    name: readFirstDefined(flow, ["name", "flow_name", "flowName", "title"]),
    templateName:
      readFirstDefined(flow, ["template_name", "templateName"]) ||
      readFirstDefined(template, ["name", "template_name", "templateName", "title"]),
    templateCategory:
      readFirstDefined(flow, ["template_category", "templateCategory", "category"]) ||
      readFirstDefined(template, ["category", "category_name", "categoryName", "type"]),
    status,
    eventId,
    eventName:
      readFirstDefined(flow, ["event_name", "eventName"]) ||
      readFirstDefined(event, ["name", "event_name", "eventName", "title"]),
    triggerType: readFirstDefined(flow, [
      "trigger",
      "trigger_type",
      "triggerType",
      "event_type",
      "eventType",
    ]),
    totalReplies: readAutomationFlowReplyCount(flow),
    updatedAt: readFirstDefined(flow, ["updated_at", "updatedAt", "last_updated", "lastUpdated"]),
  };
};

const normaliseEvent = (event) => ({
  ...event,
  id: readFirstDefined(event, ["id", "event_id", "eventId", "uuid"]),
  name: readFirstDefined(event, ["event_name", "eventName", "name", "title"]),
});

const normaliseEventsResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.event ||
    source.events ||
    source.live_events ||
    source.liveEvents ||
    source.data ||
    (Array.isArray(source) ? source : []);
  const items = Array.isArray(collection) ? collection : collection.data || [];

  return items.map(normaliseEvent);
};

const normaliseFlowsResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.flows ||
    source.automation_flows ||
    source.automationFlows ||
    source.data ||
    (Array.isArray(source) ? source : []);
  const items = Array.isArray(collection) ? collection : collection.data || [];

  return {
    data: items.map(normaliseFlow),
    currentPage: collection.current_page || source.current_page || 1,
    total: collection.total || source.total || items.length,
    perPage: collection.per_page || source.per_page || 7,
    from: collection.from || source.from || (items.length ? 1 : 0),
    to: collection.to || source.to || items.length,
  };
};

const formatStatus = (status) => FLOW_STATUS[status] || "Inactive";

const getApiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data || {};
  const eventError = responseData?.errors?.event_id;

  if (Array.isArray(eventError) && eventError[0]) return eventError[0];
  if (typeof eventError === "string") return eventError;
  return responseData.message || fallback;
};

const formatDate = (date) => {
  if (!date) {
    return "Not available";
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("MMM DD, YYYY") : "Not available";
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? "0" : numberValue.toLocaleString();
};

const formatEventName = (flow, eventLookup) => {
  if (flow.eventName) {
    return flow.eventName;
  }

  if (flow.eventId) {
    return eventLookup[flow.eventId] || `Event #${flow.eventId}`;
  }

  return "Not configured";
};

const AutomationFlows = () => {
  const history = useHistory();
  const { user } = LocalStorage();
  const [flows, setFlows] = useState({
    data: [],
    currentPage: 1,
    total: 0,
    perPage: 7,
    from: 0,
    to: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyWord, setSearchKeyWord] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [searchKeyWordDebounce] = useDebounce(searchKeyWord, 600);
  const [loading, setLoading] = useState(false);
  const [actionFlowId, setActionFlowId] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [events, setEvents] = useState([]);
  const [flowSummary, setFlowSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const hasFlows = useMemo(() => flows.data && flows.data.length > 0, [flows.data]);
  const eventLookup = useMemo(() => {
    return events.reduce((lookup, event) => ({
      ...lookup,
      [event.id]: event.name,
    }), {});
  }, [events]);

  const handleSearch = (query) => {
    setSearchKeyWord(query);
    setCurrentPage(1);
  };

  const loadFlowSummary = useCallback(async (isMounted = () => true) => {
    try {
      setSummaryLoading(true);
      const response = await fetchAutomationFlows({ page: 1, per_page: 1000 });
      const collection = normaliseFlowsResponse(response.data);
      const statusCounts = collection.data.reduce((counts, flow) => {
        if (flow.status === "active") counts.active += 1;
        else counts.inactive += 1;
        return counts;
      }, { active: 0, inactive: 0 });

      if (isMounted()) {
        setFlowSummary({
          total: collection.total,
          ...statusCounts,
        });
      }
    } catch (error) {
      if (isMounted()) setFlowSummary((previous) => previous);
    } finally {
      if (isMounted()) setSummaryLoading(false);
    }
  }, []);

  const loadFlows = useCallback(async (isMounted = () => true) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 7,
        search: searchKeyWordDebounce,
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (eventFilter) {
        params.event_id = eventFilter;
      }

      const response = await fetchAutomationFlows(params);

      if (isMounted()) {
        setFlows(normaliseFlowsResponse(response.data));
        setApiAvailable(true);
      }
    } catch (error) {
      if (isMounted()) {
        setFlows({
          data: [],
          currentPage: 1,
          total: 0,
          perPage: 7,
          from: 0,
          to: 0,
        });
        setApiAvailable(false);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [currentPage, eventFilter, searchKeyWordDebounce, statusFilter]);

  useEffect(() => {
    let mounted = true;

    loadFlows(() => mounted);

    return () => {
      mounted = false;
    };
  }, [loadFlows]);

  useEffect(() => {
    let mounted = true;
    loadFlowSummary(() => mounted);
    return () => { mounted = false; };
  }, [loadFlowSummary]);

  useEffect(() => {
    let mounted = true;

    const loadEvents = async () => {
      try {
        const response = await fetchLiveEvents({
          per_page: 1000,
          page: 1,
          user_id: user?.id,
        });

        if (mounted) {
          setEvents(normaliseEventsResponse(response.data));
        }
      } catch (error) {
        if (mounted) {
          setEvents([]);
        }
      }
    };

    loadEvents();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleEventFilterChange = (event) => {
    setEventFilter(event.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchKeyWord("");
    setStatusFilter("");
    setEventFilter("");
    setCurrentPage(1);
  };

  const handlePublish = async (flow) => {
    try {
      setActionFlowId(flow.id);
      await publishAutomationFlow(flow.id);
      toast.success("Automation flow published.");
      await loadFlows();
      await loadFlowSummary();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to publish automation flow."));
    } finally {
      setActionFlowId(null);
    }
  };

  const handlePause = async (flow) => {
    try {
      setActionFlowId(flow.id);
      await pauseAutomationFlow(flow.id);
      toast.success("Automation flow paused.");
      await loadFlows();
      await loadFlowSummary();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to pause automation flow."));
    } finally {
      setActionFlowId(null);
    }
  };

  const handleDuplicate = async (flow) => {
    try {
      setActionFlowId(flow.id);
      const response = await duplicateAutomationFlow(flow.id);
      const duplicate = normaliseFlow(
        response?.data?.data?.flow ||
        response?.data?.flow ||
        response?.data?.automation_flow ||
        response?.data?.data ||
        response?.data ||
        {}
      );

      if (!duplicate.id) throw new Error("Duplicate response did not include a flow ID.");

      toast.success("Automation flow duplicated.");
      history.push(`/conversions/automation-flows/${duplicate.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to duplicate automation flow."));
    } finally {
      setActionFlowId(null);
    }
  };

  const renderActionButtons = (flow) => {
    const isBusy = actionFlowId === flow.id;
    const isPublished = flow.status === "active";

    return (
      <>
        <button
          type="button"
          className="flow-action-button view"
          onClick={() => history.push(`/conversions/automation-flows/${flow.id}`)}
        >
          View/Edit
        </button>
        <button
          type="button"
          className="flow-action-button view"
          disabled={isBusy}
          onClick={() => handleDuplicate(flow)}
        >
          {isBusy ? "Updating..." : "Duplicate"}
        </button>
        {isPublished ? (
          <button
            type="button"
            className="flow-action-button pause"
            disabled={isBusy}
            onClick={() => handlePause(flow)}
          >
            {isBusy ? "Updating..." : "Pause"}
          </button>
        ) : (
          <button
            type="button"
            className="flow-action-button publish"
            disabled={isBusy}
            onClick={() => handlePublish(flow)}
          >
            {isBusy ? "Updating..." : "Publish"}
          </button>
        )}
      </>
    );
  };

  const percentage = (value) => flowSummary.total ? ((value / flowSummary.total) * 100).toFixed(1) : "0.0";
  const summaryCards = [
    { label: "Total Flows", value: flowSummary.total, detail: "All time", icon: <FiGitBranch />, tone: "blue" },
    { label: "Active", value: flowSummary.active, detail: `${percentage(flowSummary.active)}% of total`, icon: <FiPlayCircle />, tone: "green" },
    { label: "Inactive", value: flowSummary.inactive, detail: `${percentage(flowSummary.inactive)}% of total`, icon: <FiPauseCircle />, tone: "gold" },
  ];
  const tabs = [
    ["Overview", "/conversions"],
    ["Conversations", "/conversions/conversations"],
    ["Notifications", "/conversions/notifications"],
    ["Analytics", "/conversions/analytics"],
    ["My Automation Flows", "/conversions/automation-flows", true],
    ["Browse Templates", "/conversions/sms-automation-templates"],
    ["Billing", "/conversions/billing"],
  ];

  return (
    <SmsAccessGuard>
      <Layout>
        <main className="page automation-flows-dashboard-page">
          <section className="automation-flows-hero">
            <header>
              <div><h1>My Automation Flows</h1><p>View and manage SMS automation flows created from templates.</p></div>
              <div className="automation-flow-hero-actions">
                <Link to="/conversions/sms-automation-templates" className="create-flow-button"><FiPlus /> Create Automation Flow</Link>
                <Link to="/conversions/sms-automation-templates" className="browse-flow-button"><FiFileText /> Browse Templates</Link>
              </div>
            </header>
            <div className="automation-flow-summary-grid">
              {summaryCards.map((card) => (
                <article className="automation-flow-summary-card" key={card.label}>
                  <span className={`automation-flow-summary-icon ${card.tone}`}>{card.icon}</span>
                  <div><span>{card.label}</span><strong>{summaryLoading ? "…" : formatNumber(card.value)}</strong><small>{card.detail}</small></div>
                </article>
              ))}
            </div>
          </section>

          <nav className="conversations-tabs automation-flows-tabs" aria-label="Conversations sections">
            {tabs.map(([label, path, active]) => <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>)}
          </nav>

          <section className="automation-flow-table-card">
            <div className="automation-flow-filters">
              <label><input value={searchKeyWord} onChange={(event) => handleSearch(event.target.value)} placeholder="Search flows by name or event..." aria-label="Search automation flows" /><FiSearch /></label>
              <select value={statusFilter} onChange={handleStatusFilterChange} aria-label="Automation flow status">
                <option value="">All Statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
              </select>
              <select value={eventFilter} onChange={handleEventFilterChange} aria-label="Automation flow event">
                <option value="">All Events</option>{events.map((event) => <option key={event.id} value={event.id}>{event.name}</option>)}
              </select>
              <button type="button" onClick={clearFilters} disabled={!searchKeyWord && !statusFilter && !eventFilter}>Clear filters</button>
            </div>

            <div className="automation-flow-table-wrap">
              <Table>
                <TableHeader><tr><th>Flow Name</th><th>Template</th><th>Event</th><th>Status</th><th>Total Replies</th><th>Last Updated</th><th>Actions</th></tr></TableHeader>
                <TableBody>
                  {loading ? <tr><td colSpan={7}><div className="automation-flow-table-state"><span className="spinner-border" /></div></td></tr> : null}
                  {!loading && hasFlows ? flows.data.map((flow) => (
                    <tr key={flow.id}>
                      <td><div className="automation-flow-name"><span><FiGitBranch /></span><strong>{flow.name || "Untitled Flow"}</strong></div></td>
                      <td><div>{flow.templateName || "No template linked"}</div><small>{flow.templateCategory || "Uncategorized"}</small></td>
                      <td>{formatEventName(flow, eventLookup)}</td>
                      <td><span className={`automation-flow-status status-${flow.status}`}>{formatStatus(flow.status)}</span></td>
                      <td>{formatNumber(flow.totalReplies)}</td><td>{formatDate(flow.updatedAt)}</td>
                      <td><div className="flow-dashboard-actions">{renderActionButtons(flow)}</div></td>
                    </tr>
                  )) : null}
                  {!loading && !hasFlows ? <tr><td colSpan={7}><div className="automation-flow-table-state empty"><p>{apiAvailable ? "No automation flows yet. Start from a template." : "Automation flows are unavailable right now."}</p><Link to="/conversions/sms-automation-templates">Browse Templates</Link></div></td></tr> : null}
                </TableBody>
              </Table>
            </div>

            {!loading && flows.total > 0 ? <div className="automation-flow-pagination"><TablePagination currentPage={flows.currentPage} total={flows.total} perPage={flows.perPage} from={flows.from} to={flows.to} onChange={(page) => setCurrentPage(page)} /></div> : null}
          </section>

          <ToastContainer position="bottom-right" autoClose={2500} />
        </main>
      </Layout>
    </SmsAccessGuard>
  );
};

export { SMS_AUTOMATION_FLOW_ENDPOINT };
export default AutomationFlows;
